<template>
  <div class="cover-picture" :class="{ loading: loading }">
    <div class="cover-picture__dragscroll" v-show="isDraggable" ref="dragcontainer"
         :style="{ height: containerHeight + 'px' }">
      <img :src="src" alt="" class="draggable" ref="dragitem"/>
    </div>
    <div class="cover-picture__drag-overlay" v-show="isDraggable" :style="{ height: containerHeight + 'px' }">
      <div class="cover-picture__info" v-show="!loading">
        <i class="fas fa-arrows-alt-v"></i> Drag to reposition
      </div>
    </div>
    <div class="cover-picture__drag-handler" v-show="isDraggable" @touchstart="handleDragStart"
         @touchend="handleDragEnd" @touchmove="handleDrag" @mousedown="handleDragStart" @mouseup="handleDragEnd"
         @mousemove="handleDrag" :class="{ 'cover-picture__drag-handler--active': positioning.active }"
         ref="draghandler" :style="{ 'min-height': containerHeight + 'px' }">
    </div>
    <div class="cover-picture__actions">
      <label class="btn cover-picture__input-button">
        Upload image
        <input type="file" @change="fileChange" :value="file" :name="coverType+'[cover_picture]'"
               class="cover-picture__file-input" accept=".gif,.jpg,.jpeg,.png"/>
      </label>
      <input type="hidden" :name="coverType+'[cover_picture_destroy]'" :value="toBeDeleted"/>
      <button :disabled="loading" v-show="isRemovable" class="btn cover-picture__destroy-button"
              @click.prevent="setToBeDeleted(true)">
        Remove image
      </button>
      <button :disabled="loading" v-show="isSaveable" class="btn btnRed cover-picture__save-button"
              @click.prevent="savePicture">
        Save image
      </button>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'

  const totalHeight = 720;
  const totalWidth = 2560;
  const DRAG_HANDLER_SELECTOR = ".cover-picture__drag-handler";

  export default {
    name: "CoverPicture",

    props: {
      placeholderSrc: String,
      imageSrc: String,
      coverType: {
        type: String,
        required: true
      },
      endPoint: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        yOffset: 0,
        toBeDeleted: false,
        isSaveable: false,
        src: null,
        file: "",
        fileData: "",
        loading: false,
        containerHeight: 0.0,
        positioning: {
          active: false,
          currentY: null,
          initialY: null,
          xOffset: 0,
          yOffset: 0
        }
      };
    },

    computed: {
      isRemovable() {
        return this.src && this.src.length && this.src !== this.placeholderSrc;
      },
      isDraggable() {
        return this.isRemovable && this.src && this.src.length;
      }
    },

    mounted() {
      this.src = this.imageSrc;
      this.bindEventListeners();
      setTimeout(this.recalculateHeight, 0);
    },

    beforeDestroy() {
      this.removeEventListeners();
    },

    methods: {
      setToBeDeleted(value) {
        this.toBeDeleted = value;
        this.isSaveable = !value;
        if (value) {
          this.file = "";
          this.src = this.placeholderSrc;
        }
        this.$emit('cover_picture_destroy', value);
      },

      showAlert(type, message) {
        this.$eventHub.$emit("addAlert", {
          type: type,
          message: message
        });
      },

      fileChange(event) {
        this.loading = true;
        let file = event.currentTarget.files[0];
        let reader = new FileReader();

        this.file = event.currentTarget.value;
        this.fileData = file;

        reader.addEventListener(
          "load",
          () => {
            this.loading = false;
            this.src = reader.result;
          },
          false
        );

        if (file) {
          reader.readAsDataURL(file);
        }
        this.setToBeDeleted(false);
        this.resetPositioning();
      },

      resetPositioning() {
        this.positioning.currentY = 0;
        this.positioning.initialY = 0;
        this.positioning.yOffset = 0;
        this.setTranslate(0, 0);
      },

      savePicture() {
        this.loading = true;
        let formData = new FormData();
        if (this.file && this.fileData) {
          formData.append("uploaded_cover", this.fileData, this.file);
        }
        formData.append("pixel_offset", this.totalOffsetForFullSizeImage());
        this.postPicture(formData);
        this.isSaveable = false;
      },

      postPicture(formData) {
        axios
          .post(this.endPoint, formData)
          .then(response => {
            this.src = response.data.url;
            this.showAlert("success", response.data.message);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            this.showAlert("error", error.data.message);
          });
      },

      dragStart(event) {
        document.addEventListener("click", this.dragEnd);

        if (event.type === "touchstart") {
          this.positioning.initialY =
            event.touches[0].clientY - this.positioning.yOffset;
        } else {
          this.positioning.initialY = event.clientY - this.positioning.yOffset;
        }

        if (event.target === this.$refs.draghandler) {
          event.preventDefault();
          this.positioning.active = true;
        }
      },

      dragEnd() {
        this.positioning.initialY = parseInt(this.positioning.currentY, 10);
        this.positioning.active = false;
        this.isSaveable = true;
      },

      drag() {
        this.setCurrentY();
        this.positioning.yOffset = parseInt(this.positioning.currentY, 10);
        this.yOffset = -parseInt(this.positioning.currentY, 10);
        this.setTranslate(this.positioning.xOffset, this.positioning.currentY);
        document.removeEventListener("click", this.dragEnd);
      },

      setCurrentY() {
        let currentY = this.getEventY(event);

        if (currentY > 0) {
          this.positioning.currentY = 0;
        } else if (
          -currentY >=
          this.$refs.dragitem.clientHeight - this.containerHeight
        ) {
          this.positioning.currentY =
            this.containerHeight - this.$refs.dragitem.clientHeight;
        } else {
          this.positioning.currentY = currentY;
        }
      },

      getEventY(event) {
        let currentY;
        if (event.type === "touchmove" || event.type === "touchstart") {
          currentY = event.touches[0].clientY - this.positioning.initialY;
        } else {
          currentY = event.clientY - this.positioning.initialY;
        }
        return currentY;
      },

      recalculateHeight() {
        let container = this.$refs.dragcontainer;
        if (!container) return;
        let hwRatio = totalHeight / totalWidth;
        this.containerHeight = container.clientWidth * hwRatio;
      },

      setTranslate(xPos, yPos) {
        this.$refs.dragitem.style.transform =
          "translate3d(" + xPos + "px, " + yPos + "px, 0)";
      },

      handleDragStart(event) {
        if (!event.target.matches(DRAG_HANDLER_SELECTOR)) return;
        if (this.loading) return;
        this.dragStart(event);
      },

      handleDrag(event) {
        if (!event.target.matches(DRAG_HANDLER_SELECTOR)) return;
        if (this.loading) return;
        if (!this.positioning.active) return;
        this.drag(event);
      },

      handleDragEnd(event) {
        if (!event.target.matches(DRAG_HANDLER_SELECTOR)) return;
        this.dragEnd(event);
      },

      bindEventListeners() {
        window.addEventListener("resize", this.recalculateHeight);
      },

      removeEventListeners() {
        window.removeEventListener("resize", this.recalculateHeight);
      },

      totalOffsetForFullSizeImage() {
        return Math.abs(
          Math.ceil(
            (this.positioning.yOffset * totalWidth) /
            this.$refs.dragitem.clientWidth
          )
        );
      }
    },

    watch: {
      isDraggable() {
        setTimeout(this.recalculateHeight, 0);
      }
    }
  }
</script>

<style lang="scss" scoped>
  .cover-picture {
    position: relative;
    margin-bottom: 24px;

    &__input-button, &__destroy-button, &__save-button {
      width: 128px;
      margin-bottom: 16px;
      margin-right: 8px;
      display: inline-block;
    }

    &__destroy-checkbox, &__file-input {
      width: 0;
      height: 0;
      overflow: hidden;
      display: inline-block;
      opacity: 0;
    }

    &__dragscroll {
      overflow: hidden;
      margin-bottom: 12px;
      overflow: hidden;
      border-radius: 3px;
      border: 1px solid $ash-gray;
      height: calc(100% * 7 / 64);
      max-height: calc(100% * 7 / 64);
    }

    &__drag-overlay {
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      opacity: 1;
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: center;

      .loading & {
        display: none;
      }

      &:after {
        border-radius: 3px;
        content: "";
        position: absolute;
        z-index: 1;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: rgba($black, 0.75);
        transition: all 0.2s ease-in-out;
      }
    }

    &__info {
      transition: opacity 0.2s ease-in-out;
      opacity: 1;
      flex: 0 0 auto;
      z-index: 5;
      position: relative;
      color: $white;

      i[class*="fa-"] {
        margin-right: 4px;
      }
    }

    &__drag-handler {
      cursor: ns-resize;

      .loading & {
        cursor: default;
      }

      z-index: 10;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;

      &--active {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }

    .draggable {
      width: 100%;
    }

    &:hover {
      .cover-picture__info {
        opacity: 0;
      }

      .cover-picture__drag-overlay:after {
        background-color: rgba($black, 0);
      }
    }
  }
</style>
